import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import axios from 'axios';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [shippingInfo, setShippingInfo] = useState({ shipping_charge: 0, rule_name: '', is_free: false });

  const subtotal = getCartTotal();

  // Fetch shipping info when cart total changes
  useEffect(() => {
    const fetchShipping = async () => {
      if (subtotal > 0) {
        try {
          const response = await axios.get(`${API}/shipping-rules/calculate?order_total=${subtotal}`);
          setShippingInfo({
            shipping_charge: response.data.shipping_charge,
            rule_name: response.data.rule_name,
            rule_id: response.data.rule_id,
            is_free: response.data.is_free
          });
        } catch (error) {
          console.error('Error fetching shipping:', error);
          // Fallback to default
          setShippingInfo({ shipping_charge: 100, rule_name: 'Standard Shipping', is_free: false });
        }
      }
    };
    fetchShipping();
  }, [subtotal]);

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast({
      title: "Removed from cart",
      description: `${productName} has been removed from your cart.`,
    });
  };

  const handleCheckout = () => {
    // Store shipping info in sessionStorage for checkout
    sessionStorage.setItem('shippingInfo', JSON.stringify(shippingInfo));
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 sm:py-16">
          <div className="text-center py-8 sm:py-16">
            <ShoppingBag className="w-16 h-16 sm:w-24 sm:h-24 mx-auto text-gray-400 mb-4 sm:mb-6" />
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">Your cart is empty</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Add some amazing watches to your cart!</p>
            <Link
              to="/products"
              className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg transition-colors text-sm sm:text-base"
            >
              Shop Now
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const shipping = shippingInfo.shipping_charge;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Toaster />

      <div className="bg-black text-white py-6 sm:py-8 md:py-12 relative">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">Shopping Cart</h1>
          <p className="text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">{cart.length} items in your cart</p>
        </div>
        {/* Close Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
          aria-label="Go back"
          data-testid="cart-close-btn"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </button>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {cart.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md p-3 sm:p-4 md:p-6">
                <div className="flex gap-3 sm:gap-4">
                  <Link to={`/product/${item.slug || item.id}`} className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 flex-shrink-0">
                    <img
                      src={resolveImageUrl(item.image)}
                      alt={item.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.slug || item.id}`}>
                      <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 hover:text-yellow-600 transition-colors mb-0.5 line-clamp-2">
                        {item.name}
                      </h3>
                    </Link>
                    <p className="text-xs sm:text-sm text-yellow-600 font-semibold mb-1 sm:mb-2">{item.brand}</p>
                    <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">₹{item.price.toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemove(item.id, item.name)}
                      className="text-red-500 hover:text-red-700 p-1.5 sm:p-2 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    {/* Quantity Controls */}
                    <div className="flex items-center space-x-1.5 sm:space-x-2 bg-gray-100 rounded-lg p-1.5 sm:p-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="w-5 sm:w-8 text-center font-bold text-xs sm:text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white hover:bg-gray-200 flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 sm:mt-4 flex justify-between items-center pt-3 sm:pt-4 border-t">
                  <span className="text-gray-600 text-sm sm:text-base">Subtotal:</span>
                  <span className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                    ₹{(item.price * item.quantity).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 sticky top-20 sm:top-24">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6">Order Summary</h2>
              
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm sm:text-base">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold">
                    {shippingInfo.is_free || shipping === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      `₹${shipping}`
                    )}
                  </span>
                </div>
                {shippingInfo.rule_name && (
                  <p className="text-xs text-gray-500">
                    {shippingInfo.rule_name}
                  </p>
                )}
                <div className="border-t pt-3 sm:pt-4 flex justify-between items-center">
                  <span className="text-base sm:text-lg font-bold">Total</span>
                  <span className="text-xl sm:text-2xl font-bold text-yellow-600">₹{total.toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 sm:py-4 rounded-lg transition-all duration-300 transform hover:scale-105 mb-2 sm:mb-3 text-sm sm:text-base"
              >
                Proceed to Checkout
              </button>

              <button
                onClick={() => {
                  clearCart();
                  toast({
                    title: "Cart cleared",
                    description: "All items have been removed from your cart.",
                  });
                }}
                className="w-full bg-gray-200 hover:bg-gray-300 text-black font-semibold py-2.5 sm:py-3 rounded-lg transition-colors text-sm sm:text-base"
              >
                Clear Cart
              </button>

              <Link
                to="/products"
                className="block text-center text-yellow-600 hover:text-yellow-700 font-semibold mt-3 sm:mt-4 text-sm sm:text-base"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Cart;
